// 庫存 — on hand / on hold / available, 收貨 (weighted-average cost), 盤點
// with continuous camera scanning, 調整 / 報損, and each product's movement
// log. Every change goes through adjust_stock so it is audited.
import { useEffect, useMemo, useState } from "react";
import { zhEn, dateLocale } from "./posTier.js";
import { Search, ScanLine, SlidersHorizontal, Trash2, History as HistoryIcon } from "lucide-react";
import { C, Btn, Chip, Pill, Seg, Card, Sheet, Stepper, Label, Field, Empty, money, round2, inputStyle, useWide } from "./posUi.jsx";
import { posErrorText } from "./posMath.js";
import { ProductThumb } from "./Register.jsx";
import { adjustStock, fetchStockMovements } from "../../lib/api.js";

const KIND = { sale: [zhEn("銷售", "Sale"), "pink"], refund: [zhEn("退貨入倉", "Return to stock"), "blue"], receive: [zhEn("收貨", "Receive"), "green"], count: [zhEn("盤點", "Count"), "amber"], adjust: [zhEn("調整", "Adjust"), "grey"], writeoff: [zhEn("報損", "Write-off"), "red"], opening: [zhEn("開倉", "Opening stock"), "green"], po_return: [zhEn("退貨供應商", "Return to supplier"), "red"] };

export default function Inventory({ tenant, sym, products, setProducts, heldQty, onScanOpen, focusId, clearFocus }) {
  const wide = useWide(900);
  const [mode, setMode] = useState("list");
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const tracked = useMemo(() => products.filter(p => p.trackStock), [products]);

  useEffect(() => { if (focusId) { setDetail(focusId); clearFocus?.(); } }, [focusId, clearFocus]);

  const stats = useMemo(() => ({
    skus: tracked.length,
    value: round2(tracked.reduce((s, p) => s + Math.max(0, p.stock) * p.cost, 0)),
    retail: round2(tracked.reduce((s, p) => s + Math.max(0, p.stock) * p.price, 0)),
    // Off-sale (下架) items don't raise stock alerts.
    low: tracked.filter(p => p.status !== "draft" && p.stock > 0 && p.stock <= p.lowStock).length,
    out: tracked.filter(p => p.status !== "draft" && p.stock <= 0).length,
  }), [tracked]);

  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return tracked.filter(p => (filter === "all" || (p.status !== "draft" && (filter === "low" ? p.stock > 0 && p.stock <= p.lowStock : p.stock <= 0))) &&
      (!s || p.name.toLowerCase().includes(s) || p.barcode === s || p.sku.toLowerCase().includes(s)))
      .sort((a, b) => (a.stock - a.lowStock) - (b.stock - b.lowStock));
  }, [tracked, q, filter]);

  const applyResult = (rows) => {
    const byId = Object.fromEntries((rows || []).map(r => [r.productId, r]));
    setProducts(ps => ps.map(p => byId[p.id] ? { ...p, stock: Number(byId[p.id].stock), cost: Number(byId[p.id].cost) } : p));
  };

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: wide ? "repeat(4,1fr)" : "1fr 1fr", gap: 8, marginBottom: 12 }}>
        <Stat label={zhEn("追蹤中商品", "Tracked products")} value={stats.skus} />
        <Stat label={zhEn("庫存成本值", "Stock value (cost)")} value={money(stats.value, sym)} sub={zhEn("零售值 ", "Retail value ") + money(stats.retail, sym)} />
        <Stat label={zhEn("低庫存", "Low stock")} value={stats.low} tone={stats.low ? C.amber : undefined} onClick={() => { setMode("list"); setFilter("low"); }} />
        <Stat label={zhEn("冇貨", "Out of stock")} value={stats.out} tone={stats.out ? C.red : undefined} onClick={() => { setMode("list"); setFilter("out"); }} />
      </div>
      <Seg value={mode} onChange={setMode} style={{ marginBottom: 12 }} options={[["list", zhEn("庫存", "Stock")], ["receive", zhEn("收貨", "Receive")], ["count", zhEn("盤點", "Count")], ["adjust", zhEn("調整 / 報損", "Adjust / write-off")]]} />

      {mode === "list" && (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <div style={{ flex: 1, position: "relative" }}>
              <Search size={18} color={C.faint} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
              <input value={q} onChange={e => setQ(e.target.value)} placeholder={zhEn("搜尋或掃條碼", "Search or scan a barcode")} style={{ ...inputStyle, paddingLeft: 38, borderRadius: 14 }} />
            </div>
            <button type="button" onClick={() => onScanOpen((code) => { const hit = products.find(p => p.barcode === code || p.sku === code); if (hit) { setDetail(hit.id); return "close"; } return false; }, zhEn("掃描睇庫存", "Scan to check stock"), false)} style={{ width: 46, borderRadius: 14, border: "none", background: C.grad, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}><ScanLine size={22} /></button>
          </div>
          <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            {[["all", zhEn("全部", "All")], ["low", zhEn("低庫存", "Low stock")], ["out", zhEn("冇貨", "Out of stock")]].map(([k, l]) => <Chip key={k} on={filter === k} onClick={() => setFilter(k)}>{l}</Chip>)}
          </div>
          {list.length === 0 ? <Empty icon="📦" title={tracked.length ? zhEn("冇符合嘅商品", "No matching products") : zhEn("未有追蹤庫存嘅商品", "No products with tracked stock")} />
            : (
              <div style={{ display: "grid", gridTemplateColumns: wide ? "repeat(2,minmax(0,1fr))" : "minmax(0,1fr)", gap: 8 }}>
                {list.map(p => {
                  const held = heldQty(p.id);
                  const avail = p.stock - held;
                  return (
                    <Card key={p.id} onClick={() => setDetail(p.id)} style={{ padding: 10, display: "flex", alignItems: "center", gap: 10 }}>
                      <ProductThumb p={p} size={46} radius={12} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontSize: 14, fontWeight: 900, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</p>
                        <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: C.sub }}>{zhEn("成本", "Cost")} {money(p.cost, sym)}{p.lowStock ? zhEn(" · 低於 ", " · alert below ") + p.lowStock + zhEn(" 提示", "") : ""}</p>
                      </div>
                      <div style={{ display: "flex", gap: 10, textAlign: "center" }}>
                        <Num label={zhEn("庫存", "Stock")} v={p.stock} />
                        {wide && <Num label={zhEn("掛單", "Hold")} v={held} color={held ? C.amber : C.faint} />}
                        <Num label={zhEn("可賣", "Available")} v={avail} color={avail <= 0 ? C.red : avail <= p.lowStock ? C.amber : C.green} />
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
        </>
      )}

      {mode !== "list" && <StockBatch key={mode} kind={mode} tenant={tenant} sym={sym} products={tracked} onScanOpen={onScanOpen}
        onDone={(rows) => { applyResult(rows); setMode("list"); }} />}

      {detail && <MovementSheet product={products.find(p => p.id === detail)} sym={sym} tenant={tenant} heldQty={heldQty(detail)} onClose={() => setDetail(null)} onApplied={applyResult} />}
    </div>
  );
}

const Stat = ({ label, value, sub, tone, onClick }) => (
  <Card onClick={onClick} style={{ padding: "12px 14px" }}>
    <p style={{ margin: 0, fontSize: 12, fontWeight: 900, color: C.sub }}>{label}</p>
    <p style={{ margin: "2px 0 0", fontSize: 22, fontWeight: 900, color: tone || C.ink, letterSpacing: -0.5 }}>{value}</p>
    {sub && <p style={{ margin: 0, fontSize: 11.5, fontWeight: 700, color: C.sub }}>{sub}</p>}
  </Card>
);
const Num = ({ label, v, color }) => (
  <div style={{ minWidth: 40 }}>
    <p style={{ margin: 0, fontSize: 10.5, fontWeight: 900, color: C.sub }}>{label}</p>
    <p style={{ margin: 0, fontSize: 17, fontWeight: 900, color: color || C.ink }}>{v}</p>
  </div>
);

// 收貨 / 盤點 / 調整 as a batch: scan or search to add rows, then confirm once.
function StockBatch({ kind, tenant, sym, products, onScanOpen, onDone }) {
  const [rows, setRows] = useState([]);   // {productId, qty, cost, count}
  const [q, setQ] = useState("");
  const [ref, setRef] = useState("");
  const [note, setNote] = useState("");
  const [sub, setSub] = useState("adjust");   // adjust | writeoff (調整 mode)
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const byId = useMemo(() => Object.fromEntries(products.map(p => [p.id, p])), [products]);

  const add = (p) => setRows(rs => {
    const i = rs.findIndex(r => r.productId === p.id);
    if (i >= 0) return rs.map((r, j) => j === i ? (kind === "count" ? { ...r, count: r.count + 1 } : { ...r, qty: r.qty + 1 }) : r);
    return [{ productId: p.id, qty: kind === "adjust" ? -1 : 1, cost: String(p.cost || ""), count: kind === "count" ? 1 : 0 }, ...rs];
  });
  const found = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    return products.filter(p => p.name.toLowerCase().includes(s) || p.barcode === s || p.sku.toLowerCase() === s).slice(0, 8);
  }, [products, q]);
  const scan = () => onScanOpen((code) => { const hit = products.find(p => p.barcode === code || p.sku === code); if (hit) { add(hit); return true; } return false; },
    kind === "receive" ? zhEn("連續掃描收貨", "Scan continuously to receive") : kind === "count" ? zhEn("連續掃描盤點（每掃一次 +1）", "Scan continuously to count (+1 per scan)") : zhEn("掃描調整", "Scan to adjust"));

  const title = { receive: zhEn("收貨", "Receive"), count: zhEn("盤點", "Count"), adjust: zhEn("調整 / 報損", "Adjust / write-off") }[kind];
  const totalCost = round2(rows.reduce((s, r) => s + (kind === "receive" ? r.qty * (Number(r.cost) || 0) : 0), 0));
  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const k = kind === "adjust" ? sub : kind;
      const items = rows.map(r => kind === "count" ? { productId: r.productId, count: r.count }
        : kind === "receive" ? { productId: r.productId, qty: r.qty, ...(r.cost !== "" ? { cost: Number(r.cost) } : null) }
        : { productId: r.productId, qty: sub === "writeoff" ? Math.abs(r.qty) : r.qty });
      const res = await adjustStock({ tenantId: tenant.id, kind: k, ref: ref.trim() || undefined, note: note.trim(), items });
      onDone(res);
    } catch (e) { setErr(posErrorText(e)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      {kind === "adjust" && <Seg value={sub} onChange={setSub} style={{ marginBottom: 10 }} options={[["adjust", zhEn("調整（+ / −）", "Adjust (+ / −)")], ["writeoff", zhEn("報損 / 過期", "Write-off / expired")]]} />}
      <p style={{ fontSize: 12.5, fontWeight: 700, color: C.sub, margin: "0 2px 10px", lineHeight: 1.5 }}>
        {kind === "receive" && zhEn("掃描或搜尋入貨商品，填數量同單位成本 — 成本會按加權平均更新。", "Scan or search the products you're receiving, then enter quantity and unit cost — cost updates as a weighted average.")}
        {kind === "count" && zhEn("逐件掃描（每掃一次 +1）或者直接輸入點到嘅數量。只會更新有點嘅商品。", "Scan item by item (+1 per scan) or type the counted quantity. Only products you count are updated.")}
        {kind === "adjust" && (sub === "writeoff" ? zhEn("記錄破損、過期或試用 — 庫存會減少。", "Record damaged, expired or sample items — stock goes down.") : zhEn("手動加減庫存，例如搵返或者錯數。", "Add or remove stock by hand, e.g. found items or a miscount."))}
      </p>
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1, position: "relative" }}>
          <Search size={18} color={C.faint} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={zhEn("搜尋商品", "Search products")} style={{ ...inputStyle, paddingLeft: 38, borderRadius: 14 }}
            onKeyDown={e => { if (e.key === "Enter") { const hit = products.find(p => p.barcode === q.trim() || p.sku === q.trim()); if (hit) { add(hit); setQ(""); } } }} />
        </div>
        <Btn onClick={scan} style={{ padding: "0 14px" }}><ScanLine size={20} /> {zhEn("掃描", "Scan")}</Btn>
      </div>
      {found.map(p => (
        <button key={p.id} type="button" onClick={() => { add(p); setQ(""); }} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: 8, border: "none", borderBottom: "1px solid " + C.line, background: "#fff", fontFamily: "inherit", cursor: "pointer", textAlign: "left" }}>
          <ProductThumb p={p} size={34} radius={10} /><span style={{ flex: 1, fontWeight: 800, color: C.ink }}>{p.name}</span><span style={{ fontWeight: 800, color: C.sub }}>{zhEn("存", "Stock")} {p.stock}</span>
        </button>
      ))}

      <div style={{ marginTop: 10 }}>
        {rows.length === 0 ? <Empty icon={kind === "receive" ? "📥" : kind === "count" ? "🔢" : "🛠️"} title={zhEn("未有", "No ") + title + zhEn("項目", " items")} sub={zhEn("掃描或者搜尋加入", "Scan or search to add")} />
          : rows.map((r, i) => {
            const p = byId[r.productId];
            if (!p) return null;
            const diff = kind === "count" ? r.count - p.stock : null;
            return (
              <Card key={r.productId} style={{ padding: 10, marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <ProductThumb p={p} size={40} radius={10} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 900, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</p>
                    <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: C.sub }}>
                      {zhEn("系統", "System")} {p.stock}{kind === "count" && <span style={{ color: diff === 0 ? C.green : diff > 0 ? C.blue : C.red }}> · {diff === 0 ? zhEn("啱數", "Balanced") : (diff > 0 ? zhEn("多 ", "Over ") : zhEn("少 ", "Short ")) + Math.abs(diff)}</span>}
                      {kind !== "count" && <> → {sub === "writeoff" && kind === "adjust" ? p.stock - Math.abs(r.qty) : p.stock + r.qty}</>}
                    </p>
                  </div>
                  <button type="button" onClick={() => setRows(rs => rs.filter((_, j) => j !== i))} style={{ border: "none", background: "none", color: C.faint, cursor: "pointer" }}><Trash2 size={18} /></button>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
                  {kind === "count"
                    ? <><span style={{ fontSize: 13, fontWeight: 900, color: C.sub, flex: 1 }}>{zhEn("點到", "Counted")}</span><Stepper value={r.count} min={0} onChange={v => setRows(rs => rs.map((x, j) => j === i ? { ...x, count: v } : x))} /></>
                    : <><span style={{ fontSize: 13, fontWeight: 900, color: C.sub, flex: 1 }}>{kind === "receive" ? zhEn("入貨數量", "Quantity received") : sub === "writeoff" ? zhEn("報損數量", "Quantity written off") : zhEn("加 / 減", "Add / remove")}</span>
                      <Stepper value={kind === "adjust" && sub === "writeoff" ? Math.abs(r.qty) : r.qty} min={kind === "adjust" && sub !== "writeoff" ? -9999 : 1} onChange={v => setRows(rs => rs.map((x, j) => j === i ? { ...x, qty: v === 0 && kind === "adjust" ? (x.qty > 0 ? -1 : 1) : v } : x))} /></>}
                </div>
                {kind === "receive" && (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 900, color: C.sub, flex: 1 }}>{zhEn("單位成本", "Unit cost")}</span>
                    <input inputMode="decimal" value={r.cost} onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, cost: e.target.value } : x))} style={{ ...inputStyle, width: 110, padding: "8px 10px" }} />
                  </div>
                )}
              </Card>
            );
          })}
      </div>

      {rows.length > 0 && (
        <>
          {kind === "receive" && <Field label={zhEn("供應商 / 單號（可留空）", "Supplier / reference (optional)")} value={ref} onChange={e => setRef(e.target.value)} placeholder={zhEn("例如 PO-0012", "e.g. PO-0012")} />}
          <Field label={zhEn("備註", "Note")} value={note} onChange={e => setNote(e.target.value)} placeholder={kind === "adjust" && sub === "writeoff" ? zhEn("例如 包裝破損", "e.g. damaged packaging") : ""} />
          {err && <p style={{ color: C.red, fontWeight: 800, fontSize: 13, textAlign: "center" }}>{err}</p>}
          <Btn kind="green" style={{ width: "100%", padding: 16 }} disabled={busy} onClick={submit}>
            {busy ? zhEn("處理中…", "Processing…") : zhEn("確認", "Confirm ") + title + " · " + rows.length + zhEn(" 項", " items") + (kind === "receive" && totalCost ? " · " + money(totalCost, sym) : "")}
          </Btn>
        </>
      )}
    </div>
  );
}

function MovementSheet({ product, sym, tenant, heldQty, onClose, onApplied }) {
  const [moves, setMoves] = useState(null);
  const [delta, setDelta] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    if (product) fetchStockMovements(product.id).then(m => alive && setMoves(m)).catch(() => alive && setMoves([]));
    return () => { alive = false; };
  }, [product?.id, product?.stock]);
  if (!product) return null;
  const quick = async () => {
    setBusy(true); setErr("");
    try { const r = await adjustStock({ tenantId: tenant.id, kind: "adjust", note: "快速調整", items: [{ productId: product.id, qty: delta }] }); onApplied(r); setDelta(0); }
    catch (e) { setErr(posErrorText(e)); } finally { setBusy(false); }
  };
  return (
    <Sheet open onClose={onClose} title={product.name}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
        {[[zhEn("庫存", "Stock"), product.stock, C.ink], [zhEn("掛單中", "On hold"), heldQty, C.amber], [zhEn("可賣", "Available"), product.stock - heldQty, product.stock - heldQty <= 0 ? C.red : C.green]].map(([k, v, col]) => (
          <div key={k} style={{ background: "#fff", border: "1px solid " + C.line, borderRadius: 14, padding: "10px 8px", textAlign: "center" }}>
            <p style={{ margin: 0, fontSize: 11.5, fontWeight: 900, color: C.sub }}>{k}</p>
            <p style={{ margin: 0, fontSize: 20, fontWeight: 900, color: col }}>{v}</p>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#fff", border: "1px solid " + C.line, borderRadius: 16, padding: 10, marginBottom: 14 }}>
        <SlidersHorizontal size={18} color={C.sub} />
        <span style={{ flex: 1, fontWeight: 900, color: C.ink, fontSize: 14 }}>{zhEn("快速調整", "Quick adjust")}</span>
        <Stepper value={delta} min={-9999} onChange={setDelta} />
        <Btn small disabled={!delta || busy} onClick={quick}>{zhEn("套用", "Apply")}</Btn>
      </div>
      {err && <p style={{ color: C.red, fontWeight: 800, fontSize: 13 }}>{err}</p>}
      <Label><HistoryIcon size={13} style={{ verticalAlign: -2 }} /> {zhEn("庫存記錄", "Stock history")}</Label>
      {moves === null ? <p style={{ color: C.sub, fontWeight: 800 }}>{zhEn("載入中…", "Loading…")}</p>
        : moves.length === 0 ? <p style={{ color: C.sub, fontWeight: 800 }}>{zhEn("未有記錄", "No history yet")}</p>
        : moves.map(m => {
          const k = KIND[m.kind] || [m.kind, "grey"];
          const n = Number(m.qty_change);
          return (
            <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 2px", borderBottom: "1px solid " + C.line }}>
              <Pill tone={k[1]}>{k[0]}</Pill>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 12.5, fontWeight: 800, color: C.sub }}>{new Date(m.created_at).toLocaleString(dateLocale(), { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" })}{m.note ? " · " + m.note : ""}{m.kind === "receive" && m.ref ? " · " + m.ref : ""}</p>
              </div>
              <span style={{ fontWeight: 900, color: n > 0 ? C.green : C.red }}>{n > 0 ? "+" : ""}{n}</span>
              <span style={{ fontWeight: 800, color: C.sub, minWidth: 34, textAlign: "right" }}>→ {Number(m.stock_after)}</span>
            </div>
          );
        })}
    </Sheet>
  );
}

